/**
 * Feature: Advanced Lead Tracking
 * /features/lead-tracking
 *
 * Dedicated feature page with SoftwareApplication + FAQ schema.
 * Competitor-killer positioning copy per strategic plan.
 */
import {
  FeatureFaq,
  FeatureStats,
  FeatureSignup,
  FeatureHeroBadge,
  FeatureChecklist,
} from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import {
  Target,
  MousePointerClick,
  Phone,
  MapPin,
  BarChart3,
  Brain,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';
import { ProductShowcase } from '@/components/marketing/ProductShowcase';
import { PRODUCT_FRAMES } from '@/lib/productFrames';
import { CapabilityLedger } from '@/components/marketing/CapabilityLedger';

const faqs = [
  {
    question: 'How does Rooflyzer track leads differently than other CRMs?',
    answer:
      'Most CRMs only record leads when someone enters them manually. Rooflyzer automatically tracks every touchpoint—from the first ad click to the final handshake—attributing each lead to the exact channel, campaign, and keyword that generated it. This gives you a complete acquisition funnel view, not just a contact list.',
  },
  {
    question: 'Can I see which marketing channels generate the most revenue?',
    answer:
      'Yes. Our attribution engine connects ad spend directly to closed deals. You will see cost-per-lead, cost-per-close, and ROI by channel (Google Ads, Facebook, door knocking, referrals, etc.) so you can double down on what works and cut what does not.',
  },
  {
    question: 'Does the lead tracking work with door knocking and canvassing?',
    answer:
      'Absolutely. Field leads captured via our canvassing tools are tracked with GPS coordinates and time stamps, merged into the same pipeline as digital leads. You get one unified view of every lead source.',
  },
  {
    question: 'How does lead scoring work?',
    answer:
      'Every lead is scored in real-time based on engagement signals—website visits, email opens, quote requests, phone calls, and appointment attendance. High-scoring leads are flagged for immediate follow-up, so your team focuses on buyers, not tire-kickers.',
  },
];

const leadTrackingStats = [
  {
    value: '25-40%',
    label: 'Higher Conversion Rates',
    desc: 'Through systematic follow-up',
  },
  {
    value: '2 hrs → 15 min',
    label: 'Pipeline Management',
    desc: 'Daily time reduction',
  },
  { value: '100%', label: 'Lead Attribution', desc: 'Every source tracked' },
  { value: '3x', label: 'Faster Response Time', desc: 'Automated first touch' },
];

export const LeadTrackingFeaturePage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Advanced Lead Tracking',
    description:
      'Full-funnel lead attribution for roofing contractors. Track every touchpoint from first click to closed deal. Eliminate guesswork and master your acquisition funnel.',
    featureList: [
      'Multi-channel lead attribution',
      'Smart lead scoring',
      'Automated drip sequences',
      'Visual Kanban pipeline',
      'GPS canvassing integration',
      'Cost-per-lead analytics',
    ],
    url: 'https://rooflyzer.pro/features/lead-tracking',
    offers: { price: '0', description: 'Free tier available — no credit card required' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Roofing Lead Tracking & Pipeline Management"
        description="Track every roofing lead from first contact to closed job. Rooflyzer gives you a full-funnel view with source attribution and scoring."
        canonicalUrl="/features/lead-tracking"
        ogImage="/og/pipeline.png"
        keywords="roofing lead tracking, roofing pipeline management, lead attribution, roofing CRM, roofing sales pipeline"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'Lead Tracking', url: '/features/lead-tracking' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={Target}>The Competitor Killer</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Stop Managing Leads.{' '}
              <span className="text-white underline decoration-slate-600 decoration-2 underline-offset-8">
                Master Your Funnel.
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Most CRMs are just digital filing cabinets. Rooflyzer is a hunter. While competitors
              leave lead attribution to guesswork, our system tracks every touchpoint from the first
              click to the final handshake. You aren't just managing customers; you are mastering
              your acquisition funnel.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-[#050507] hover:bg-white/90 font-semibold px-8"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Start Tracking Free
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Link to="/compare">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/20 text-white hover:bg-white/10"
                >
                  See How We Compare
                </Button>
              </Link>
            </div>
          </section>

          {/* Product film */}
          <section className="mb-24">
            <ProductShowcase frame={PRODUCT_FRAMES.pipeline} float={false} />
          </section>

          {/* Capability Grid */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Full-Funnel Lead Intelligence
            </h2>
            <CapabilityLedger
              items={[
                {
                  icon: MousePointerClick,
                  title: 'Multi-Channel Attribution',
                  desc: 'Know exactly which ad, keyword, or referral source generated each lead. No more "how did you hear about us?" guessing.',
                },
                {
                  icon: Brain,
                  title: 'Smart Lead Scoring',
                  desc: 'AI-powered scoring based on engagement signals. Focus your team on high-intent buyers, not tire-kickers.',
                },
                {
                  icon: TrendingUp,
                  title: 'Automated Drip Sequences',
                  desc: 'Email and SMS follow-up sequences that nurture cold leads into hot opportunities while you sleep.',
                },
                {
                  icon: BarChart3,
                  title: 'Pipeline Analytics',
                  desc: 'Visual Kanban board with conversion rates at every stage. Identify bottlenecks and optimize your close rate.',
                },
                {
                  icon: MapPin,
                  title: 'GPS Canvassing Integration',
                  desc: 'Door knocks tracked with coordinates and outcomes. Field leads merge into the same pipeline as digital leads.',
                },
                {
                  icon: Phone,
                  title: 'Call Tracking & Recording',
                  desc: 'Every inbound call attributed to source. Know which campaigns drive phone calls, not just form fills.',
                },
              ]}
            />
          </section>

          {/* The Problem → Solution */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Problem With Other CRMs</h2>
                <ul className="space-y-4">
                  {[
                    'Leads entered manually — data is always incomplete',
                    "No attribution — you can't tell which marketing channels work",
                    'Disjointed tools — canvassing, calls, and web leads in different systems',
                    'Reactive follow-up — leads go cold before anyone calls back',
                    'No scoring — your best rep wastes time on unqualified leads',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <span className="text-red-400 mt-0.5 text-lg">✕</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">How Rooflyzer Solves It</h2>
                <FeatureChecklist
                  items={[
                    'Every touchpoint auto-captured — from ad click to signed contract',
                    'Dollar-for-dollar ROI by marketing channel, campaign, and keyword',
                    'One unified pipeline — digital, phone, and field leads together',
                    'Automated sequences — leads get the right message at the right time',
                    'Real-time scoring tells your team who to call first',
                  ]}
                />
              </div>
            </div>
          </section>

          {/* Business Impact */}
          <FeatureStats title="Measurable Business Impact" items={leadTrackingStats} />

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* CTA */}
          <FeatureSignup
            title="Ready to Master Your Lead Funnel?"
            buttonLabel="Get Started Free — No Credit Card"
          >
            Join roofing contractors who stopped guessing and started growing.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default LeadTrackingFeaturePage;
